import { OrderInfo, PaymentInfo } from "../models/models";
import { Billing_Data, Currency_Rate, Preferences, Store } from "../models/RestModels";
import { RestService } from "../services/rest.service";
import { Utils } from "./Utils";

declare function txt2html(str:string):string;
declare function marked(str:string):string;

// Declare the global functions exposed by printer-bridge.js
declare function initPrinter(config: object): void;
declare function printXml(xmlString: string): void;


export class PrintUtils
{
	copies:number = 1;
	constructor(copies:number=1)
	{
		this.copies = copies;
	}

	/**
	 * Sends the generated ticket XML to the printer worker.
	 * @param xmlString The XML string representing the ticket.
	 */
	public printTicketXml(xmlString: string)
	{
		// The actual call to the global function will be done from the component
		// that uses this utility, after it generates the string.
		printXml(xmlString);
	}

	/**
	 * Escape special XML characters
	 */
	private escapeXml(text: string): string
	{
		if (!text) return '';
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&apos;');
	}

	/**
	 * Create ESC/POS XML ticket for thermal printer
	 */
	public createTicketXml(rest: RestService, store: Store, order_info: OrderInfo, payment_info: PaymentInfo | null, preferences: Preferences, billing_data: Billing_Data | null, columns: number = 48): string
	{
		const formatCurrency = (amount: number) => {
			return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', currencyDisplay: 'narrowSymbol' }).format(amount);
		};
		const formatNumber = (amount: number) => {
			return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(amount);
		};

		const service_type_dictionary: Record<string, string> = {
			'TOGO': 'PARA LLEVAR',
			'IN_PLACE': '',
			'PICK_UP': 'A DOMICILIO',
			'QUICK_SALE': ''
		};

		// Build address
		let address = store.address || '';
		if (store.city) address += address ? `, ${store.city.toUpperCase()}` : store.city.toUpperCase();
		if (store.state) address += address ? `, ${store.state.toUpperCase()}` : store.state.toUpperCase();
		if (store.zipcode) address += address ? `, CP ${store.zipcode}` : `CP ${store.zipcode}`;

		let rfc = store.rfc || '';
		if (billing_data) {
			address = billing_data.address || '';
			if (billing_data.city) address += address ? `, ${billing_data.city.toUpperCase()}` : billing_data.city.toUpperCase();
			if (billing_data.state) address += address ? `, ${billing_data.state.toUpperCase()}` : billing_data.state.toUpperCase();
			if (billing_data.zipcode) address += address ? `, CP ${billing_data.zipcode}` : `CP ${billing_data.zipcode}`;
			rfc = billing_data.rfc || '';
		}

		const store_business_name = store.business_name || '';
		const service_type = service_type_dictionary[order_info.order.service_type] || '';
		const order_id = order_info.order.id ? `ID: ${order_info.order.id}` : `IDOF: ${order_info.order.sync_id}`;
		const folio = order_info.order.store_consecutive ? `FOLIO: ${order_info.order.store_consecutive}` : '';
		const date_string = Utils.getDateString(order_info.order.created, true);

		// Calculate column widths for table (based on 48 columns: 24 | 10 | 14)
		const col1Width = Math.floor(columns * 0.50);  // Item name
		const col2Width = Math.floor(columns * 0.21);  // Qty x Price
		const col3Width = columns - col1Width - col2Width - 2; // Total

		let xml = '<EscPos>\n';
		xml += '  <Init/>\n';

		// For each copy
		for (let copy = 0; copy < this.copies; copy++) {
			// Header - Store info
			xml += `  <Text justification="center" fontWidth="2" fontHeight="2" bold="true">${this.escapeXml(store.name)}</Text>\n`;
			xml += '  <Feed lines="1"/>\n';

			if (store_business_name) {
				xml += `  <Text justification="center">${this.escapeXml(store_business_name)}</Text>\n`;
			}
			if (rfc) {
				xml += `  <Text justification="center">RFC: ${this.escapeXml(rfc)}</Text>\n`;
			}
			if (address) {
				xml += `  <Text justification="center">${this.escapeXml(address)}</Text>\n`;
			}
			if (store.phone) {
				xml += `  <Text justification="center">TEL: ${this.escapeXml(store.phone)}</Text>\n`;
			}
			xml += '  <Feed lines="1"/>\n';

			// Service type if applicable
			if (service_type) {
				xml += `  <Text justification="center" fontWidth="2" fontHeight="2" bold="true">${this.escapeXml(service_type)}</Text>\n`;
				xml += '  <Feed lines="1"/>\n';
			}

			// Cancelled order warning
			if (order_info.order.status === 'CANCELLED') {
				xml += '  <Text justification="center" fontWidth="2" fontHeight="2" bold="true">ORDEN CANCELADA</Text>\n';
				xml += '  <Feed lines="1"/>\n';
			}

			// Order info
			xml += `  <Text justification="left">${this.escapeXml(order_id)}</Text>\n`;
			if (folio) {
				xml += `  <Text justification="left">${this.escapeXml(folio)}</Text>\n`;
			}
			xml += `  <Text justification="left">Fecha: ${this.escapeXml(date_string)}</Text>\n`;
			xml += '  <Feed lines="1"/>\n';

			// Separator line
			xml += '  <Rule style="single"/>\n';

			// Items table header
			xml += '  <Table>\n';
			xml += '    <Columns>\n';
			xml += `      <Column width="${col1Width}" align="left" marginRight="1"/>\n`;
			xml += `      <Column width="${col2Width}" align="right" marginRight="1"/>\n`;
			xml += `      <Column width="${col3Width}" align="right"/>\n`;
			xml += '    </Columns>\n';
			xml += '    <Rows>\n';

			// Header row
			xml += '      <Row>\n';
			xml += '        <Cell><Text bold="true">Articulo</Text></Cell>\n';
			xml += '        <Cell><Text bold="true">Cant</Text></Cell>\n';
			xml += '        <Cell><Text bold="true">Total</Text></Cell>\n';
			xml += '      </Row>\n';

			// Separator row
			xml += '      <Row>\n';
			xml += '        <Cell><Rule/></Cell>\n';
			xml += '        <Cell><Rule/></Cell>\n';
			xml += '        <Cell><Rule/></Cell>\n';
			xml += '      </Row>\n';

			// Item rows
			for (const order_item_info of order_info.items) {
				let item_name = '';
				if (preferences.display_categories_on_items === 'YES' && order_item_info.category) {
					item_name = order_item_info.category.name + ' - ';
				}
				item_name += order_item_info.item.name;

				const qty = order_item_info.order_item.qty || 1;
				const unit_price = (order_item_info.order_item.total || 0) / qty;
				const total = order_item_info.order_item.total || 0;
				const isRefund = order_item_info.order_item.type === 'REFUND';
				const discount = order_item_info.order_item.discount_percent ? ` (${order_item_info.order_item.discount_percent}%)` : '';

				xml += '      <Row>\n';
				xml += `        <Cell>${this.escapeXml(item_name)}</Cell>\n`;
				xml += `        <Cell>${isRefund ? '-' : ''}${formatNumber(qty)}x${formatCurrency(unit_price)}${discount}</Cell>\n`;
				xml += `        <Cell>${isRefund ? '-' : ''}${formatCurrency(total)}</Cell>\n`;
				xml += '      </Row>\n';

				// Notes, exceptions, refund info
				if (isRefund || order_item_info.order_item.note || order_item_info.order_item.exceptions) {
					xml += '      <Row>\n';
					let notes = '';
					if (isRefund) notes += 'DEVOLUCION ';
					if (order_item_info.order_item.note) notes += order_item_info.order_item.note + ' ';
					if (order_item_info.order_item.exceptions) notes += order_item_info.order_item.exceptions;
					xml += `        <Cell>${this.escapeXml(notes.trim())}</Cell>\n`;
					xml += '        <Cell></Cell>\n';
					xml += '        <Cell></Cell>\n';
					xml += '      </Row>\n';
				}
			}

			xml += '    </Rows>\n';
			xml += '  </Table>\n';

			// Separator
			xml += '  <Rule style="single"/>\n';

			// Discount if any
			if (order_info.order.discount) {
				xml += `  <Text justification="right">Descuento: ${formatCurrency(order_info.order.discount)}</Text>\n`;
			}

			// Total
			const total_amount = order_info.order.total - (order_info.order.discount || 0);
			xml += `  <Text justification="right" fontWidth="2" fontHeight="2" bold="true">TOTAL: ${formatCurrency(total_amount)}</Text>\n`;
			xml += '  <Feed lines="1"/>\n';

			// Payment info
			if (payment_info?.movements) {
				xml += `  <Text justification="right">Recibido: ${formatCurrency(payment_info.payment.received_amount)}</Text>\n`;
				xml += `  <Text justification="right" bold="true">Cambio: ${formatCurrency(payment_info.payment.change_amount)} MXN</Text>\n`;
				xml += '  <Feed lines="1"/>\n';
			}

			// QR code for billing if applicable
			if (order_info.order.paid_status === 'PAID' && order_info.store.default_billing_data_id && order_info.order.facturacion_code) {
				xml += `  <Text justification="center">Codigo de facturacion:</Text>\n`;
				xml += `  <Text justification="center" bold="true">${this.escapeXml(order_info.order.facturacion_code)}</Text>\n`;
				xml += '  <Feed lines="1"/>\n';
				xml += `  <QrCode justification="center" size="4">${this.escapeXml(order_info.order.facturacion_code)}</QrCode>\n`;
				xml += '  <Feed lines="1"/>\n';
			}

			// Footer text
			if (order_info.store.ticket_footer_text) {
				xml += `  <Text justification="center">${this.escapeXml(order_info.store.ticket_footer_text)}</Text>\n`;
				xml += '  <Feed lines="1"/>\n';
			}

			// Final feeds and cut
			xml += '  <Feed lines="3"/>\n';
			xml += '  <Cut mode="partial"/>\n';

			// Add separator between copies
			if (copy < this.copies - 1) {
				xml += '  <Feed lines="2"/>\n';
			}
		}

		xml += '</EscPos>';
		return xml;
	}

	/**
	 * Create a simple test ticket XML
	 */
	public createTestTicketXml(storeName: string = 'TIENDA DE PRUEBA', columns: number = 48): string
	{
		const now = new Date();
		const dateStr = Utils.getDateString(now, true);

		let xml = '<EscPos>\n';
		xml += '  <Init/>\n';
		xml += `  <Text justification="center" fontWidth="2" fontHeight="2" bold="true">${this.escapeXml(storeName)}</Text>\n`;
		xml += '  <Feed lines="1"/>\n';
		xml += '  <Text justification="center">TICKET DE PRUEBA</Text>\n';
		xml += `  <Text justification="center">${this.escapeXml(dateStr)}</Text>\n`;
		xml += '  <Feed lines="1"/>\n';
		xml += '  <Rule style="single"/>\n';

		// Sample items
		xml += '  <Table>\n';
		xml += '    <Columns>\n';
		xml += '      <Column width="24" align="left" marginRight="1"/>\n';
		xml += '      <Column width="10" align="right" marginRight="1"/>\n';
		xml += '      <Column width="12" align="right"/>\n';
		xml += '    </Columns>\n';
		xml += '    <Rows>\n';
		xml += '      <Row>\n';
		xml += '        <Cell><Text bold="true">Articulo</Text></Cell>\n';
		xml += '        <Cell><Text bold="true">Cant</Text></Cell>\n';
		xml += '        <Cell><Text bold="true">Total</Text></Cell>\n';
		xml += '      </Row>\n';
		xml += '      <Row><Cell><Rule/></Cell><Cell><Rule/></Cell><Cell><Rule/></Cell></Row>\n';
		xml += '      <Row>\n';
		xml += '        <Cell>Producto de Prueba 1</Cell>\n';
		xml += '        <Cell>2 x $50</Cell>\n';
		xml += '        <Cell>$100.00</Cell>\n';
		xml += '      </Row>\n';
		xml += '      <Row>\n';
		xml += '        <Cell>Producto de Prueba 2</Cell>\n';
		xml += '        <Cell>1 x $75</Cell>\n';
		xml += '        <Cell>$75.00</Cell>\n';
		xml += '      </Row>\n';
		xml += '    </Rows>\n';
		xml += '  </Table>\n';

		xml += '  <Rule style="single"/>\n';
		xml += '  <Text justification="right" fontWidth="2" fontHeight="2" bold="true">TOTAL: $175.00</Text>\n';
		xml += '  <Feed lines="1"/>\n';
		xml += '  <Text justification="center">Gracias por su compra</Text>\n';
		xml += '  <Feed lines="1"/>\n';
		xml += '  <QrCode justification="center" size="4">https://example.com/test</QrCode>\n';
		xml += '  <Feed lines="3"/>\n';
		xml += '  <Cut mode="partial"/>\n';
		xml += '</EscPos>';

		return xml;
	}

	public createTicketString(rest:RestService, store:Store, order_info:OrderInfo, payment_info:PaymentInfo, preferences:Preferences, billing_data:Billing_Data|null ):string
	{
		let currency_formater = new Intl.NumberFormat('us-EN', { style: 'currency', currency: 'USD', currencyDisplay:"narrowSymbol" });
		let number_formater = new Intl.NumberFormat('us-EN', { maximumSignificantDigits: 2,} ); // useGrouping:"always"

		let formatCurrency = (amount:number)=>currency_formater.format(amount);
		let formatNumber = (amount:number)=>number_formater.format(amount);

		let service_type_dictionary = {
			'TOGO':'PARA LLEVAR',
			'IN_PLACE':'',
			'PICK_UP':'A DOMICILIO',
		};

		let currency_rate:Currency_Rate | null = null;

		if( payment_info?.movements )
		{
			dance:
			for(let bmi of payment_info.movements)
			{
				for(let bmo of bmi.bank_movement_orders )
				{
					if( bmo.currency_id != order_info.store.default_currency_id )
					{
						currency_rate = {
							store_id: order_info.store.id,
							currency_id: bmo.currency_id,
							rate: bmo.exchange_rate
						};
						break dance;
					}
				}
			}
		}

		let html:string = '';
		let address = store.address;


		address += (address && store.city ? (','+store.city.toUpperCase()):'');
		address += (address && store.state ? (','+store.city.toUpperCase()):'');
		address += (address && store.state ? (','+store.state.toUpperCase()):'');
		address += (address && store.zipcode ? (','+store.zipcode):'');

		let zipcode = store.zipcode ? ('CP:'+store.zipcode || ''):'';

		let rfc = store.rfc;


		if( billing_data )
		{
			address = billing_data.address || '';
			address += (address && billing_data.city ? (','+billing_data.city.toUpperCase()):'');
			address += (address && billing_data.state ? (','+billing_data.city.toUpperCase()):'');
			address += (address && billing_data.state ? (','+billing_data.state.toUpperCase()):'');
			address += (address && billing_data.zipcode ? (','+billing_data.zipcode):'');
			zipcode = billing_data.zipcode ? ('CP:'+txt2html(billing_data.zipcode) || ''):'';

			rfc = billing_data.rfc;
		}

		zipcode += store.phone ? ' TEL:'+txt2html(store.phone) : '';

		let store_bussiness_name = store.business_name || '';
		let phone = store.phone? ('&nbsp;TEL: '+txt2html(store.phone )|| ''):'';

		let image_path = rest.getImagePath(order_info.store.ticket_image_id, rest.local_preferences.default_ticket_image_id);

		let tipo = 	service_type_dictionary[order_info.order.service_type];

		let order_title_html = tipo?`<h2 class="text-center text-black">${tipo}</h2>` : '';

		if( order_info.order.status == 'CANCELLED' )
		{
			order_title_html = `<h1 class="text-center">ORDEN CANCELLADA</h1>`
		}


		let order_id_string =  order_info.order.id
				? ('<b>ID:</b>'+order_info.order.id)
				: ('<b>IDOF</b>'+order_info.order.sync_id);

		let folio = order_info.order.store_consecutive ? ('<b>FOLIO</b>:'+order_info.order.store_consecutive ) : '';


		let items_html = '';

		for(let order_item_info of order_info.items)
		{
			let item_name = '';

			if( preferences.display_categories_on_items == 'YES'&&order_item_info.category  )
				item_name = order_item_info.category.name+' - ';

			item_name+= order_item_info.item.name;
			let border_top = !(order_item_info.order_item.item_option_id) ? 'border-top' : '';

			let discount = order_item_info.order_item.discount_percent ? '('+order_item_info.order_item.discount_percent+'%)': '';

			items_html += `
				<tr class="${border_top}">
					<td class="truncate td_first">
						${txt2html(item_name)}
					</td>
					<td class="text-end monospace td_other">
						${order_item_info.order_item.type == 'REFUND'?'-':''}
						${formatNumber( order_item_info.order_item.qty )}×${formatCurrency((order_item_info.order_item.total || 0 )/(order_item_info.order_item.qty || 1))}
						${discount}
					</td>
					<td class="text-end monospace td_other">
						${order_item_info.order_item.type == 'REFUND'?'-':''}
						${formatCurrency(order_item_info.order_item.total)}
					</td>
				</tr>
				`
			if( order_item_info.order_item.type == 'REFUND' || order_item_info.order_item.note || order_item_info.order_item.exceptions )
			{
				items_html += `
					<tr>
						<td colspan=3 class="text-start monospace">
							${order_item_info.order_item.type == 'REFUND'?'<div>DEVOLUCIÓN</div>':''}
							${order_item_info.order_item.note?`<div>${txt2html(order_item_info.order_item.note)}</div>`:''}
							${order_item_info.order_item.exceptions?`<div>${txt2html(order_item_info.order_item.exceptions)}</div>`:''}
						</td>
					</tr>`;
			}
		}

		if( order_info.order.discount )
		{
			items_html += `
				<tr class="border-top">
					<td class="text-end monospace td_first">Descuento</td>
					<td class="td_other"></td>
					<td class="text-end monospace td_other">${formatCurrency(order_info.order.discount)}</td>
				</tr>`;
		}

		items_html += `
				<tr class="border-top">
					<td class="text-end fw-bold td_first">Total</td>
					<td class="text-center td_other"></td>
					<td class="text-end fw-bold monospace td_other">
						${formatCurrency(order_info.order.total-order_info.order.discount)}
					</td>
				</tr>`;

		if(payment_info?.movements)
		{
			items_html+= `
				<tr class="border-top">
					<td class="text-end">Total Recibido</td>
					<td class="text-center"></td>
					<td class="text-end monospace">${formatCurrency(payment_info.payment.received_amount)}</td>
				</tr>
				<tr class="mx-0">
					<td class="text-end fw-bold">Cambio</td>
					<td class="text-end fw-bold monospace" colspan=2>${formatCurrency(payment_info.payment.change_amount)}MXN</td>
				</tr>`

		}

		if( currency_rate )
		{
			items_html =`
			<tr class="mx-0">
				<td class="text-end fw-bold">Cambio en ${currency_rate.currency_id}</td>
				<td class="text-end fw-bold monospace" colspan=2>${formatCurrency( payment_info.payment.change_amount/currency_rate.rate)} USD</td>
			</tr>
			`;
		}

		if( order_info.order.status == 'CLOSED' && order_info.order.paid_status != 'PAID'  && (order_info.order.total-order_info.order.amount_paid >0.01) )
		{
			items_html += `
			<div>
				A través de este pagaré yo
				<b>${txt2html( order_info?.client?.name || '')}</b>, me comprometo a pagar incondicionalmente la cantidad de
				<b>${formatCurrency( order_info.order.total-(order_info.order.amount_paid+order_info.order.discount))}</b>
				de la venta con el folio No. ${order_info.order.id} a la orden
				de
				<b>${txt2html(order_info?.store?.rfc)}</b>. Dicha cantidad será pagada el día ______ de _________ del ______, de la
				forma
				en la que lo solicite el beneficiario de este documento, y en caso de que no se realice tal precisión, en el
				domicilio
				del suscriptor.
				<br>
				<b>${txt2html(order_info.store.city+' '+order_info.store.state)}</b>, a
				<b>${Utils.getDateString( order_info.order.created, true )}</b>
				<br>
				<br>
			</div>
			`
		}
		html += `
			<style>
				.px-0{
					padding-left: 0;
					padding-right: 0;
					text-center:
				}
				.text-center{
					text-align: center;
				}
				.text-end{
					text-align: right;
				}
				.w-100
				{
					width:100%;
				}
				.border-bottom
				{
					border-bottom: 1px solid #AAAAAA;
				}

				.fw-bold
				{
					font-weight: bold;
				}
				.border-top
				{
					border-top: 1px solid #AAAAAA;
				}
				.break_pages
				{
					filter: grayscale(100%);
					break-after: page;
				}

				.td_first
				{
					width:33%;
					overflow: hidden;
				}

				.td_other
				{
					width:33%;
				}

				@media screen{
					.image_logo_ticket
					{
						width: 25%;
						padding-bottom: 15px;
					}
				}

				@media print {
					.image_logo_ticket
					{
						width: 80%;
						padding-bottom: 15px;
					}
				}

				a {
					color: #000;
					text-decoration: none;
				}

				#markdown a:visited,
				#markdown a:link,
				#markdown a:hover,
				#markdown a:focus
				{
					color: #000 !important;
					text-decoration: none !important;
				}

				.truncate
				{
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

			</style>
			<div class="print_ticket_container">
		`;

		let footer = '';
		if( order_info.order.paid_status == 'PAID' && order_info.store.default_billing_data_id )
		{
			footer += `
			<div class="text-center">
				Código:
				<code style="font-size:20px;color:#000000;text-transform:uppercase">${order_info.order.facturacion_code}</code>
			</div>
			`
		}
		if( order_info.store.ticket_footer_text )
		{
			footer+=`<div id="markdown" class="text-center">${marked( order_info.store.ticket_footer_text )}</div>`;
		}


		for(let i=0;i<this.copies;i++)
		{
			html += `
				<div class="break_pages px-0">
					<div class="text-center">
						<img src="${image_path}" class="image_logo_ticket">
					</div>
					<div style="text-transform:uppercase">
						<div class="fw-bold text-center">
							Sucursal ${txt2html(store.name)}
						</div>
						<div class="text-center pb-2">
							<div>${txt2html(store_bussiness_name ||'')}</div
							<div>${rfc}</div>
							<span>${txt2html(address)}</span>
							<div>
								<span>${zipcode}</span>
							</div>
						</div>
					</div>
					${order_title_html}
					<table>
						<tr>
							<td>
								<div>${order_id_string}</div>
								<div>${folio}</div>
							</td>
							<td class="text-end">
								Fecha:<br>
								<b>${Utils.getDateString(order_info.order.created, true)}</b>
							</td>
						</tr>
					</table>

					<table class="mx-0" style="table-layout: fixed; width: 100%;">
						<tr class="border-bottom">
							<td class="fw-bold border-bottom td_first truncate">Artículo</td>
							<td class="fw-bold text-center border-bottom td_other truncate">Cantidad</td>
							<td class="fw-bold text-end border-bottom td_other truncate">Total</td>
						</tr>
						${items_html}
					</table>
					${footer}
				</div>
			`
		}
		return `<ticket>${html}</ticket>`;
	}
}
